import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const tabList = [
  { key: 'details', label: 'Details' },
  { key: 'messages', label: 'Messages' },
  { key: 'files', label: 'Files' },
];

const AdminRequestDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('details');
  const [request, setRequest] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    const fetchRequest = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('service_requests')
        .select('*')
        .eq('id', id)
        .single();
      if (!error) setRequest(data);
      setLoading(false);
    };
    if (id) fetchRequest();
  }, [id]);

  useEffect(() => {
    const fetchMessages = async () => {
      const { data, error } = await supabase
        .from('request_message')
        .select('*')
        .eq('request_id', id)
        .order('id', { ascending: true });
      if (!error) setMessages(data || []);
    };
    if (id) fetchMessages();
  }, [id]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!newMessage.trim() && !file) || !user) return;

    let fileUrl = '';
    if (file) {
      const { data: fileData, error: fileError } = await supabase.storage
        .from('messages')
        .upload(`${id}/${file.name}`, file);
      if (!fileError) {
        const { data: { publicUrl } } = supabase.storage
          .from('messages')
          .getPublicUrl(`${id}/${file.name}`);
        fileUrl = publicUrl;
      }
    }

    const { error } = await supabase
      .from('request_message')
      .insert([
        { request_id: id, message: newMessage, sender: 'admin', sender_id: user.id, file_url: fileUrl }
      ]);

    if (!error) {
      setNewMessage('');
      setFile(null);
      const { data } = await supabase
        .from('request_message')
        .select('*')
        .eq('request_id', id)
        .order('id', { ascending: true });
      setMessages(data || []);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (!request) return <div>Request not found.</div>;

  return (
    <div className="max-w-3xl mx-auto bg-white rounded-lg shadow p-8 mt-8">
      <h1 className="text-2xl font-bold mb-6">Request Details</h1>
      <div className="mb-6 flex space-x-4 border-b">
        {tabList.map(tab => (
          <button
            key={tab.key}
            className={`pb-2 px-4 font-medium border-b-2 transition-colors ${activeTab === tab.key ? 'border-primary text-primary' : 'border-transparent text-gray-500'}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {activeTab === 'details' && (
        <div>
          <div className="mb-4">
            <strong>Type:</strong> {request.type}
          </div>
          <div className="mb-4">
            <strong>Status:</strong> {request.status}
          </div>
          <div className="mb-4">
            <strong>Created At:</strong> {new Date(request.created_at).toLocaleString()}
          </div>
          <div className="mb-4">
            <strong>Brand ID:</strong> {request.brand_id}
          </div>
          <div className="mb-4">
            <strong>Answers:</strong>
            <pre className="bg-gray-100 p-2 rounded mt-2 text-sm">{JSON.stringify(request.answers, null, 2)}</pre>
          </div>
        </div>
      )}
      {activeTab === 'messages' && (
        <div>
          <div className="mb-4 max-h-64 overflow-y-auto space-y-4">
            {messages.map((msg) => (
              <div key={msg.id} className={`p-3 rounded-lg ${msg.sender === 'admin' ? 'bg-blue-50 text-blue-900 ml-auto' : 'bg-gray-100 text-gray-900 mr-auto'} max-w-xs`}> 
                <div>{msg.message}</div>
                {msg.file_url && (
                  <div className="mt-2">
                    {msg.file_url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                      <img src={msg.file_url} alt="attachment" className="max-w-xs max-h-40 rounded" />
                    ) : (
                      <a href={msg.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Download File</a>
                    )}
                  </div>
                )}
                <div className="text-xs mt-1 text-right">{new Date(msg.created_at).toLocaleTimeString()}</div>
              </div>
            ))}
          </div>
          <form onSubmit={handleSendMessage} className="flex items-center mt-4 space-x-2">
            <input
              type="text"
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="flex-1 border rounded px-3 py-2"
            />
            <input
              type="file"
              onChange={e => setFile(e.target.files?.[0] || null)}
              className="border rounded px-2 py-1"
            />
            <button type="submit" className="bg-primary text-white px-4 py-2 rounded">Send</button>
          </form>
        </div>
      )}
      {activeTab === 'files' && (
        <div>
          <h2 className="text-lg font-semibold mb-4">Files</h2>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!file || !user) return;
              let fileUrl = '';
              const { data: fileData, error: fileError } = await supabase.storage
                .from('messages')
                .upload(`${id}/${file.name}`, file);
              if (!fileError) {
                const { data: { publicUrl } } = supabase.storage
                  .from('messages')
                  .getPublicUrl(`${id}/${file.name}`);
                fileUrl = publicUrl;
                await supabase.from('request_message').insert([
                  { request_id: id, message: '', sender: 'admin', sender_id: user.id, file_url: fileUrl }
                ]);
                const { data } = await supabase
                  .from('request_message')
                  .select('*')
                  .eq('request_id', id)
                  .order('id', { ascending: true });
                setMessages(data || []);
                setFile(null);
              }
            }}
            className="flex items-center mb-6 space-x-2"
          >
            <input
              type="file"
              onChange={e => setFile(e.target.files?.[0] || null)}
              className="border rounded px-2 py-1"
            />
            <button type="submit" className="bg-primary text-white px-4 py-2 rounded">Upload</button>
          </form>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {messages.filter(msg => msg.file_url).map(msg => (
              <div key={msg.id} className="bg-gray-100 rounded-lg p-3 flex flex-col items-center">
                {msg.file_url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                  <img src={msg.file_url} alt="attachment" className="max-h-32 mb-2 rounded" />
                ) : (
                  <div className="flex flex-col items-center mb-2">
                    <span className="material-icons text-4xl text-gray-400">insert_drive_file</span>
                    <span className="text-xs mt-1">{msg.file_url.split('/').pop()}</span>
                  </div>
                )}
                <a href={msg.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-xs mt-2">Download</a>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRequestDetails; 